import { useState, useEffect, useRef } from 'react';

interface VerifySessionModalProps {
  counterName: string;
  session: any;
  onClose: () => void;
  onConfirm: (transferedAmount: number) => Promise<void>;
}

export default function VerifySessionModal({
  counterName,
  session,
  onClose,
  onConfirm,
}: VerifySessionModalProps) {
  const openingCash = Number(session?.openingCash ?? 0);
  const sales = Number(session?.totalSalesAmount ?? session?.salesAmount ?? 0);
  const cashIn = Number(session?.cashIn ?? 0);
  const cashOut = Number(session?.cashOut ?? 0);
  const cashAmount = Number(session?.totalReceivedCash ?? session?.receivedCash ?? 0);
  const bankMobile = Number(session?.totalReceivedBankAmount ?? session?.receivedBankAmount ?? 0);
  const salesReturn = Number(session?.totalSalesReturnAmount ?? session?.salesReturnAmount ?? 0);
  const refund = Number(session?.totalSalesRefundAmount ?? session?.salesRefundAmount ?? 0);
  const creditApplied = Number(session?.totalAdjustedCreditAmount ?? session?.adjustedCreditAmount ?? 0);
  const closingCash = Number(session?.closingCash ?? 0);
  const cashShortOrExcess = Number(session?.cashShortOrExcess ?? 0);
  const defaultTransfer = Number(session?.transferedAmount ?? closingCash ?? 0);

  const [transferAmount, setTransferAmount] = useState(String(defaultTransfer));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const handleConfirmRef = useRef<() => void>(() => {});

  useEffect(() => {
    handleConfirmRef.current = handleConfirm;
  });

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !isSubmitting) handleConfirmRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isSubmitting]);

  const handleConfirm = async () => {
    const amount = parseFloat(transferAmount);
    if (transferAmount === '' || isNaN(amount) || amount < 0) {
      setError('Please enter a valid transfer amount.');
      return;
    }
    setError('');
    setIsSubmitting(true);
    try {
      await onConfirm(amount);
    } catch (err: any) {
      setError(err?.message || 'Verification failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Sticky Header */}
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <i className="ri-verified-badge-line text-xl text-gray-700"></i>
            <h2 className="text-lg font-semibold text-gray-900">
              Verify Session of {counterName}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer disabled:opacity-50"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-2 gap-4">
            {/* Left: Session Summary */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-information-line text-blue-600"></i>
                <p className="text-sm font-medium text-blue-900">Session Summary</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Opening Cash:</span>
                  <span className="text-sm font-medium text-gray-900">{openingCash.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-green-600">+ Sales:</span>
                  <span className="text-sm font-medium text-green-600">{sales.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-green-600">+ Cash In:</span>
                  <span className="text-sm font-medium text-green-600">{cashIn.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-red-600">- Cash Out:</span>
                  <span className="text-sm font-medium text-red-600">{cashOut.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Cash Amount:</span>
                  <span className="text-sm font-medium text-gray-900">{cashAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-red-600">- Bank/Mobile:</span>
                  <span className="text-sm font-medium text-red-600">{bankMobile.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-red-600">- Sales Return:</span>
                  <span className="text-sm font-medium text-red-600">{salesReturn.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-red-600">- Refund:</span>
                  <span className="text-sm font-medium text-red-600">{refund.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Credit Applied:</span>
                  <span className="text-sm font-medium text-gray-900">{creditApplied.toFixed(2)}</span>
                </div>
                <div className="pt-2 border-t border-gray-300 flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-900">Closing Cash:</span>
                  <span className="text-lg font-bold text-gray-900">{closingCash.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Cash Short/Excess:</span>
                  <span className={`text-sm font-medium ${cashShortOrExcess > 0 ? 'text-green-600' : cashShortOrExcess < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {cashShortOrExcess > 0 ? '+' : ''}{cashShortOrExcess.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Transfer Amount */}
            <div className="flex flex-col gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transfer Amount to Outlet <span className="text-red-500">*</span>
                </label>
                <input
                  type="number" onWheel={(e) => e.currentTarget.blur()}
                  value={transferAmount}
                  onChange={(e) => {
                    setTransferAmount(e.target.value);
                    setError('');
                  }}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  disabled={isSubmitting}
                  autoFocus
                  className="w-full px-3 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-2xl font-bold disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-2">Enter the cash amount to transfer to the outlet</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500"></i>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="flex gap-3 p-4 border-t bg-gray-50 sticky bottom-0">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-gray-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line mr-1 animate-spin"></i>
                Verifying...
              </>
            ) : (
              'Verify'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
